// Drag-and-drop move of a root task within or between task sections.
//
// `tasks` is the project's flat root-task list in render order (Projects.tsx buckets
// it by section_id without re-sorting). A move can land on another task (placed
// before it when crossing sections; same-section drags keep the original reorder
// semantics) or on a section header / empty section body (placed last).
//
// Only the source and destination sections are renumbered, each 0..n-1 in render
// order — the old handler renumbered the whole project by flat array position,
// which rewrote list_index in sections the drag never touched.

export interface MovableTask {
  id: string;
  section_id: string | null;
  list_index?: number | null;
}

export type MoveTarget = { taskId: string } | { sectionId: string | null };

export interface TaskIndexChange {
  id: string;
  list_index: number;
  /** Present only when the task changed section. */
  section_id?: string | null;
}

export function moveTask<T extends MovableTask>(
  tasks: T[],
  dragId: string,
  target: MoveTarget,
): { tasks: T[]; changes: TaskIndexChange[] } | null {
  const fromIdx = tasks.findIndex(t => t.id === dragId);
  if (fromIdx === -1) return null;
  const dragged = tasks[fromIdx];
  const sourceSection = dragged.section_id ?? null;

  let destSection: string | null;
  let next: T[];

  if ("taskId" in target) {
    if (target.taskId === dragId) return null;
    const toIdx = tasks.findIndex(t => t.id === target.taskId);
    if (toIdx === -1) return null;
    destSection = tasks[toIdx].section_id ?? null;
    next = [...tasks];
    const [moved] = next.splice(fromIdx, 1);
    if (destSection === sourceSection) {
      next.splice(toIdx, 0, moved); // unchanged same-section behavior
    } else {
      const beforeIdx = next.findIndex(t => t.id === target.taskId);
      next.splice(beforeIdx, 0, { ...moved, section_id: destSection });
    }
  } else {
    destSection = target.sectionId ?? null;
    const inDest = tasks.filter(t => (t.section_id ?? null) === destSection);
    if (destSection === sourceSection && inDest[inDest.length - 1]?.id === dragId) return null;
    next = tasks.filter(t => t.id !== dragId);
    let lastIdx = -1;
    next.forEach((t, i) => { if ((t.section_id ?? null) === destSection) lastIdx = i; });
    const insertAt = lastIdx === -1 ? next.length : lastIdx + 1;
    next.splice(insertAt, 0, { ...dragged, section_id: destSection });
  }

  const touched = new Set<string | null>([sourceSection, destSection]);
  const counters = new Map<string | null, number>();
  const changes: TaskIndexChange[] = [];
  const renumbered = next.map(t => {
    const sec = t.section_id ?? null;
    if (!touched.has(sec)) return t;
    const idx = counters.get(sec) ?? 0;
    counters.set(sec, idx + 1);
    const orig = tasks.find(o => o.id === t.id)!;
    const sectionChanged = (orig.section_id ?? null) !== sec;
    if (sectionChanged || orig.list_index !== idx) {
      changes.push(sectionChanged ? { id: t.id, section_id: sec, list_index: idx } : { id: t.id, list_index: idx });
    }
    return t.list_index === idx ? t : { ...t, list_index: idx };
  });

  return { tasks: renumbered, changes };
}
